import { fetchActionFactory } from '../../../shared/store/fetchFactory';
import {
  fetchingError,
  fetchTaxYears,
  selectTaxYear,
  selectReportingCurrencyCode,
} from '../../../shared/store/actions';
import {
  reportingCurrencyCodeSelector,
} from '../../../shared/store/selectors';
import {
  errorNotification,
} from '../../../shared/store/notificationActions';
import { YES_FLAG } from '../../../shared/constants';
import { TAX_CURRENCY } from '../constants';

import {
  fetchSlGroupsInitialData as fetchSlGroupsInitialDataApi,
  fetchSlGroupItems as fetchSlGroupItemsApi,
  fetchTaxTypesForGroup as fetchTaxTypesForGroupApi,
  fetchReportDataForGroup as fetchReportDataForGroupApi,
  fetchReportingCurrencyCodesForGroup as fetchReportingCurrencyCodesForGroupApi,
} from '../api';

import {
  fetchTbeErrors as fetchTbeErrorsApi,
} from '../../api';

import {
  FETCHING_SL_GROUPS_INITIAL_DATA,
  FETCHING_SL_GROUPS_INITIAL_DATA_SUCCESS,
  FETCHING_SL_GROUPS_INITIAL_DATA_FAILURE,
  FETCHING_SL_GROUP_ITEMS,
  FETCHING_SL_GROUP_ITEMS_SUCCESS,
  FETCHING_SL_GROUP_ITEMS_FAILURE,
  CHANGE_SL_GROUP_ITEM,
  CHANGE_PERIOD_FROM,
  CHANGE_PERIOD_TO,
  FETCHING_COUNTRY_TAX_TYPES,
  FETCHING_COUNTRY_TAX_TYPES_SUCCESS,
  FETCHING_COUNTRY_TAX_TYPES_FAILURE,
  CHANGE_COUNTRY_TAX_TYPE,
  FETCHING_REPORT_DATA_FOR_GROUP,
  FETCHING_REPORT_DATA_FOR_GROUP_SUCCESS,
  FETCHING_REPORT_DATA_FOR_GROUP_FAILURE,
  CHANGE_BOOK_TYPE,
  FETCHING_REPORTING_CURRENCY_CODES_FOR_GROUP,
  FETCHING_REPORTING_CURRENCY_CODES_FOR_GROUP_SUCCESS,
  FETCHING_REPORTING_CURRENCY_CODES_FOR_GROUP_FAILURE,
  FETCH_TBE_ERRORS,
} from './types';

import { changeSlGroupType } from './sharedActions';

export const fetchReportingCurrencyCodesForGroup = (groupType, groupItem) => async (dispatch, getState) => {
  dispatch({ type: FETCHING_REPORTING_CURRENCY_CODES_FOR_GROUP });

  try {
    const reportingCurrencyCodes = await fetchReportingCurrencyCodesForGroupApi({ groupType, groupItem });

    const selectedCurrencyCode = reportingCurrencyCodeSelector(getState());
    const isSelectedCurrencyCodeStillValid
      = reportingCurrencyCodes.find(({ value }) => value === selectedCurrencyCode);

    if (!isSelectedCurrencyCodeStillValid) {
      dispatch(selectReportingCurrencyCode(TAX_CURRENCY));
    }

    dispatch({
      type: FETCHING_REPORTING_CURRENCY_CODES_FOR_GROUP_SUCCESS,
      reportingCurrencyCodes,
    });
  } catch (error) {
    dispatch({
      type: FETCHING_REPORTING_CURRENCY_CODES_FOR_GROUP_FAILURE,
    });

    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};

export const fetchTbeErrors = fetchActionFactory(
  FETCH_TBE_ERRORS,
  fetchTbeErrorsApi,
  { field: 'tbeErrors' }
);

export const fetchReportDataForGroup = (groupType, groupItem, countryTaxType) => async dispatch => {
  dispatch({ type: FETCHING_REPORT_DATA_FOR_GROUP });

  try {
    const { bookTypes, currencyCode } = await fetchReportDataForGroupApi({ groupType, groupItem, countryTaxType });

    dispatch({
      type: FETCHING_REPORT_DATA_FOR_GROUP_SUCCESS,
      bookTypes,
      currencyCode,
    });
  } catch (error) {
    dispatch({
      type: FETCHING_REPORT_DATA_FOR_GROUP_FAILURE,
    });

    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};

export const fetchSlGroupsInitialData = ({
  slGroupType,
  slGroupItem,
  includeFbosCTT,
  periodName,
} = {}) => async (dispatch, getState) => {
  const countryTaxType = getState().balanceSheetManagement.aggregation.countryTaxType;

  dispatch(fetchTbeErrors(periodName));
  dispatch(fetchTaxYears());
  dispatch(fetchReportingCurrencyCodesForGroup(
    slGroupType && slGroupType.value,
    slGroupItem && slGroupItem.value
  ));

  dispatch({ type: FETCHING_SL_GROUPS_INITIAL_DATA });

  try {
    const {
      groupTypes: slGroupTypes,
      groupItems: slGroupItems,
      countryTaxTypes,
      bookTypes,
      currencyCode,
    } = await fetchSlGroupsInitialDataApi({
      periodName,
      groupType: slGroupType && slGroupType.value,
      groupItem: slGroupItem && slGroupItem.value,
      countryTaxType: slGroupItem && countryTaxType && countryTaxType.value,
      includeFbosCTT,
    });

    const defaultSlGroupType = slGroupTypes.find(({ defaultFlag }) => defaultFlag === YES_FLAG) || slGroupTypes[0];

    const nextSlGroupType = (typeof slGroupType === 'object' && slGroupType?.value)
      ? slGroupType : { value: defaultSlGroupType.value, label: defaultSlGroupType.name };

    const slGroupItemExists = slGroupItem
      && slGroupItems.length
      && slGroupItems.find(({ groupItem }) => groupItem === slGroupItem.value);
    const nextSlGroupItem = slGroupItemExists
      ? slGroupItem
      : null;

    dispatch({
      type: FETCHING_SL_GROUPS_INITIAL_DATA_SUCCESS,
      slGroupTypes,
      slGroupType: nextSlGroupType,
      slGroupItems,
      slGroupItem: nextSlGroupItem,
      countryTaxTypes,
      countryTaxType: slGroupItem && countryTaxType,
      bookTypes,
      currencyCode,
    });

    if (countryTaxType && nextSlGroupType && nextSlGroupItem) {
      dispatch(fetchReportDataForGroup(nextSlGroupType.value, nextSlGroupItem.value, countryTaxType.value));
    }
  } catch (error) {
    dispatch({
      type: FETCHING_SL_GROUPS_INITIAL_DATA_FAILURE,
    });

    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};

export const fetchSlGroupItems = ({ periodName, groupType }) => async dispatch => {
  dispatch({ type: FETCHING_SL_GROUP_ITEMS });
  try {
    const slGroupItems = await fetchSlGroupItemsApi({ periodName, groupType });

    dispatch({
      type: FETCHING_SL_GROUP_ITEMS_SUCCESS,
      slGroupItems,
    });
  } catch (error) {
    dispatch({
      type: FETCHING_SL_GROUP_ITEMS_FAILURE,
    });

    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};

export const fetchTaxTypesForGroup = (groupType, groupItem, periodFrom) => async dispatch => {
  dispatch({ type: FETCHING_COUNTRY_TAX_TYPES });

  try {
    const countryTaxTypes = await fetchTaxTypesForGroupApi({ groupType, groupItem, periodFrom });

    dispatch({
      type: FETCHING_COUNTRY_TAX_TYPES_SUCCESS,
      countryTaxTypes,
    });
  } catch (error) {
    dispatch({
      type: FETCHING_COUNTRY_TAX_TYPES_FAILURE,
    });

    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};

export const changeSlGroupTypeAndFetch = ({ groupType, periodName }) => dispatch => {
  dispatch(changeSlGroupType(groupType));
  dispatch(fetchSlGroupItems({ groupType: groupType.value, periodName }));
};

export const changeSlGroupItem = (slGroupType, slGroupItem, periodFrom) => async (dispatch, getState) => {
  dispatch({ type: CHANGE_SL_GROUP_ITEM, slGroupItem });
  await dispatch(fetchTaxTypesForGroup(slGroupType.value, slGroupItem.value, periodFrom));
  dispatch(fetchReportingCurrencyCodesForGroup(slGroupType.value, slGroupItem.value));

  const countryTaxType = getState().balanceSheetManagement.aggregation.countryTaxType;
  if (countryTaxType) {
    dispatch(fetchReportDataForGroup(slGroupType.value, slGroupItem.value, countryTaxType.value));
  }
};

export const changePeriodFrom = periodFrom => dispatch => {
  dispatch({ type: CHANGE_PERIOD_FROM, periodFrom });
};

export const changePeriodTo = ({ periodTo, groupType }) => dispatch => {
  dispatch({ type: CHANGE_PERIOD_TO, periodTo });
  dispatch(fetchSlGroupItems({
    periodName: periodTo ? periodTo.value : null,
    groupType: groupType ? groupType.value: null,
  }));
};

export const changeCountryTaxType = (countryTaxType, slGroupType, slGroupItem) => dispatch => {
  dispatch({ type: CHANGE_COUNTRY_TAX_TYPE, countryTaxType });

  if (slGroupItem) {
    dispatch(fetchReportDataForGroup(slGroupType.value, slGroupItem.value, countryTaxType.value));
  }
};

export const changeBookType = bookType => dispatch => {
  dispatch({ type: CHANGE_BOOK_TYPE, bookType });
};

export const changeTaxYear = selectTaxYear;
